import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { motion, useMotionValueEvent, useScroll } from 'framer-motion';
import { useAppContext } from '../context/AppContext';
import { useAppStoreData } from '../hooks/useAppStoreData';
import { findProject, requestLaunch } from '../utils/simulator';
import { Container, SectionShell, hover, pressable, radius, text } from '../styles/GlobalStyles';
import SectionHeader from './common/SectionHeader';
import Reveal from './common/Reveal';
import { PlayIcon } from './common/Icons';

// Timeline geometry. Sticky dates rest at STICKY_TOP, so their node's centre — the rail's "now" — sits at ANCHOR.
const STICKY_TOP = 96;
const YEAR_LINE = 26;
const NODE = 11;
const ANCHOR = STICKY_TOP + YEAR_LINE / 2;

const List = styled.div`
  --rail-gap: 44px;
  --role-top: 40px;
  position: relative;
  border-bottom: 1px solid ${props => props.theme.colors.border};

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    --rail-gap: 28px;
    --role-top: 32px;
  }
`;

// Runs from the first node to the end of the list; the fill's tip stays on the anchor while you scroll.
const Rail = styled.div`
  position: absolute;
  top: calc(var(--role-top) + ${YEAR_LINE / 2}px);
  bottom: 0;
  left: ${(NODE - 1) / 2}px;
  width: 1px;
  background: ${props => props.theme.colors.borderStrong};
`;

const Fill = styled(motion.div)`
  position: absolute;
  inset: 0;
  background: ${props => props.theme.colors.primary};
  transform-origin: top;
`;

const Role = styled.article`
  display: grid;
  grid-template-columns: minmax(0, 3fr) minmax(0, 8fr);
  gap: 20px 64px;
  padding: var(--role-top) 0 48px var(--rail-gap);
  border-top: 1px solid ${props => props.theme.colors.border};

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: minmax(0, 1fr);
    padding-bottom: 40px;
  }
`;

const Aside = styled.div`
  align-self: start;
  position: sticky;
  top: ${STICKY_TOP}px;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    position: relative;
    top: 0;
  }
`;

const pulse = keyframes`
  from {
    transform: scale(1);
    opacity: 0.5;
  }
  to {
    transform: scale(2.6);
    opacity: 0;
  }
`;

const Node = styled.span<{ $reached: boolean; $current: boolean }>`
  position: absolute;
  top: ${(YEAR_LINE - NODE) / 2}px;
  left: calc(-1 * var(--rail-gap));
  width: ${NODE}px;
  height: ${NODE}px;
  border-radius: 50%;
  background: ${props => props.theme.colors.background};
  box-shadow: inset 0 0 0 1.5px ${props => (props.$reached ? props.theme.colors.primary : props.theme.colors.borderStrong)};
  transition: box-shadow 200ms ease;

  // The role you're in now keeps a slow, quiet pulse once the timeline reaches it.
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: ${props => props.theme.colors.primary};
    opacity: 0;
  }

  @media (prefers-reduced-motion: no-preference) {
    ${props =>
      props.$current &&
      props.$reached &&
      css`
        &::after {
          animation: ${pulse} 2.4s ease-out infinite;
        }
      `}
  }
`;

const Dot = styled(motion.span)`
  position: absolute;
  inset: 3px;
  border-radius: 50%;
  background: ${props => props.theme.colors.primary};
`;

const Years = styled.div<{ $reached: boolean }>`
  font-size: ${text.lg};
  font-weight: 600;
  line-height: ${YEAR_LINE}px;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
  color: ${props => (props.$reached ? props.theme.colors.text : props.theme.colors.textTertiary)};
  transition: color 200ms ease;
`;

const Meta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 8px;
  font-size: ${text.sm};
  color: ${props => props.theme.colors.textTertiary};

  time {
    font-variant-numeric: tabular-nums;
    color: ${props => props.theme.colors.textSecondary};
  }

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    flex-direction: row;
    flex-wrap: wrap;
    gap: 2px 12px;
  }
`;

const Heading = styled.h3`
  font-size: ${text.xl};
  font-weight: 600;
  letter-spacing: -0.02em;
  line-height: 1.2;
  color: ${props => props.theme.colors.text};

  span {
    color: ${props => props.theme.colors.textTertiary};
    font-weight: 400;
  }
`;

const Description = styled.p`
  max-width: 64ch;
  margin-top: 14px;
  line-height: 1.65;
  color: ${props => props.theme.colors.textSecondary};
  text-wrap: pretty;
`;

const Achievements = styled.ul`
  list-style: none;
  max-width: 64ch;
  margin: 24px 0 28px;
  display: flex;
  flex-direction: column;
  gap: 10px;

  li {
    display: grid;
    grid-template-columns: 20px minmax(0, 1fr);
    font-size: ${text.sm};
    line-height: 1.6;
    color: ${props => props.theme.colors.text};

    &::before {
      content: '–';
      color: ${props => props.theme.colors.textTertiary};
    }
  }
`;

const Projects = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const ProjectTag = styled.button<{ $interactive: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 32px;
  padding: 0 10px 0 ${props => (props.$interactive ? '4px' : '10px')};
  border-radius: ${radius.md};
  border: 1px solid ${props => props.theme.colors.border};
  background: transparent;
  font-size: ${text.xs};
  font-weight: 500;
  color: ${props => props.theme.colors.text};
  cursor: ${props => (props.$interactive ? 'pointer' : 'default')};
  ${props => props.$interactive && pressable}

  img,
  i {
    width: 24px;
    height: 24px;
    border-radius: 5.5px;
    background: ${props => props.theme.colors.backgroundAlt};
  }

  svg {
    color: ${props => props.theme.colors.textTertiary};
    opacity: 0;
    transition: opacity 150ms ease;
  }

  ${hover} {
    &:hover {
      background: ${props => (props.$interactive ? props.theme.colors.backgroundAlt : 'transparent')};
    }

    &:hover svg {
      opacity: 1;
    }
  }
`;

// "09/2022 - 11/2024" or "11/2024 - Present": an open end means the role runs to today.
const parsePeriod = (period: string) => {
  const [start, end] = period.split(/\s+[-–]\s+/).map(part => part.match(/(\d{1,2})\/(\d{4})/));
  const now = new Date();
  const from = start ? { month: Number(start[1]), year: Number(start[2]) } : { month: now.getMonth() + 1, year: now.getFullYear() };
  const to = end ? { month: Number(end[1]), year: Number(end[2]) } : { month: now.getMonth() + 1, year: now.getFullYear() };
  return { from, to, current: !end, months: Math.max(1, (to.year - from.year) * 12 + to.month - from.month) };
};

const formatDuration = (months: number, language: string) => {
  const years = Math.floor(months / 12);
  const rest = months % 12;
  const parts =
    language === 'tr'
      ? [years && `${years} yıl`, rest && `${rest} ay`]
      : [years && `${years} year${years > 1 ? 's' : ''}`, rest && `${rest} month${rest > 1 ? 's' : ''}`];
  return parts.filter(Boolean).join(' ');
};

const Experience: React.FC = () => {
  const { content, language } = useAppContext();
  const { experience, projects } = content;
  const ids = useMemo(() => projects.items.map(item => item.appStoreId), [projects.items]);
  const { apps } = useAppStoreData(ids);

  const railRef = useRef<HTMLDivElement>(null);
  const nodeRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const [reached, setReached] = useState<boolean[]>([]);
  const { scrollYProgress } = useScroll({ target: railRef, offset: [`start ${ANCHOR}px`, `end ${ANCHOR}px`] });

  // A role counts as reached once its node has come up to the anchor.
  const measure = useCallback(() => {
    const next = nodeRefs.current.map(node => !!node && node.getBoundingClientRect().top + NODE / 2 <= ANCHOR + 1);
    setReached(previous => (previous.length === next.length && previous.every((value, index) => value === next[index]) ? previous : next));
  }, []);

  useMotionValueEvent(scrollYProgress, 'change', measure);
  useEffect(measure, [measure, experience.items.length]);

  return (
    <SectionShell id="experience">
      <Container>
        <SectionHeader index={2} title={experience.title} />

        <List>
          <Rail ref={railRef} aria-hidden="true">
            <Fill style={{ scaleY: scrollYProgress }} />
          </Rail>

          {experience.items.map((item, index) => {
            const { from, to, current, months } = parsePeriod(item.period);
            const isReached = !!reached[index];
            const end = current ? (language === 'tr' ? 'Bugün' : 'Present') : to.year;
            return (
              <Reveal key={item.company}>
                <Role>
                  <Aside>
                    <Node
                      ref={element => {
                        nodeRefs.current[index] = element;
                      }}
                      $reached={isReached}
                      $current={current}
                      aria-hidden="true"
                    >
                      <Dot initial={false} animate={{ scale: isReached ? 1 : 0 }} transition={{ type: 'spring', duration: 0.4, bounce: 0.35 }} />
                    </Node>
                    <Years $reached={isReached}>{from.year === end ? from.year : `${from.year} — ${end}`}</Years>
                    <Meta>
                      <time>{item.period}</time>
                      <span>{formatDuration(months, language)}</span>
                      <span>{item.location}</span>
                    </Meta>
                  </Aside>

                  <div>
                    <Heading>
                      {item.title} <span>· {item.company}</span>
                    </Heading>
                    <Description>{item.description}</Description>

                    <Achievements>
                      {item.achievements.map(achievement => (
                        <li key={achievement}>{achievement}</li>
                      ))}
                    </Achievements>

                    <Projects>
                      {item.projects.map(name => {
                        const project = findProject(projects.items, name);
                        const icon = project ? apps[project.appStoreId]?.icon : undefined;
                        return (
                          <ProjectTag
                            key={name}
                            type="button"
                            $interactive={!!project}
                            title={project ? projects.simulator.run : undefined}
                            onClick={() => project && requestLaunch(project.appStoreId)}
                          >
                            {project && (icon ? <img src={icon} alt="" /> : <i />)}
                            {project?.name ?? name}
                            {project && <PlayIcon size={9} />}
                          </ProjectTag>
                        );
                      })}
                    </Projects>
                  </div>
                </Role>
              </Reveal>
            );
          })}
        </List>
      </Container>
    </SectionShell>
  );
};

export default Experience;
